// apps/vendor/src/pages/auth/RegisterSurveyPage.tsx
//
// Phase 1 Wave 9 Task 46 — Dynamic survey form (vendor mirror).
// Sets `role='vendor'` so GET /auth/survey/config?role=vendor returns the
// vendor onboarding question set. POST /auth/survey response carries
// `newStatus: 'PendingApproval'` for vendors — toast copy reflects
// the pending-approval state and we still navigate to /login (login gate
// will block until admin approves).
//
// Implements:
//   - CONTEXT.md — survey gate; merchant transitions Unverified→Active
//                            vendor transitions Unverified→PendingApproval (BE).
//   - CONTEXT.md — Idempotency-Key header (anon-scoped) per submit.
//
// Flow:
//   1. GET /auth/survey/config?role={merchant|vendor}  — render dynamic form
//   2. User answers required questions (validation per question.required + type)
//   3. POST /auth/survey { email, surveyId, version, answers[] } — Idempotency-Key
//   4. On success → navigate /login with success toast
//
// Question types (per API-CONTRACTS L483-489):
//   single  → radio group (label + options[])
//   multi   → checkbox group (label + options[])
//   text    → <input type="text"> or <textarea> when label hints longform
//   number  → <input type="number">
//
// NOTE on brief mismatch:
//   The Task 46 brief lists "text/select/multiselect/textarea/rating/boolean"
//   per Task 8 SurveyConfig types. The shipping API contract (Task 8/25 BE)
//   serves only `single | multi | text | number`. We render against the
//   contract — extending later with `rating`/`boolean`/`textarea` is a
//   schema bump (+ admin CRUD update) handled by FIX-12 in a future task.

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
  useToast,
} from '@printway/ui';

import { useTranslation, t as i18nT } from '../../lib/i18n/index.js';

// ---------------------------------------------------------------------------
// Types — mirror GetAuthSurveyConfigResponse (API-CONTRACTS L478-494)
// ---------------------------------------------------------------------------

type QuestionType = 'single' | 'multi' | 'text' | 'number';

interface SurveyQuestion {
  id: string;
  type: QuestionType;
  label: string;
  required: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}

interface SurveyConfig {
  surveyId: string;
  version: number;
  questions: SurveyQuestion[];
}

type AnswerValue = string | number | string[];
type AnswerMap = Record<string, AnswerValue | undefined>;

// ---------------------------------------------------------------------------
// Page component (role-scoped — used by both merchant + vendor mirrors)
// ---------------------------------------------------------------------------

export function RegisterSurveyPage(): JSX.Element {
  return <RegisterSurveyPageRoleScoped role="vendor" />;
}

interface RoleScopedProps {
  role: 'merchant' | 'vendor';
}

function RegisterSurveyPageRoleScoped({ role }: RoleScopedProps): JSX.Element {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('auth');

  const [config, setConfig] = useState<SurveyConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const email = useMemo<string>(() => {
    try {
      return sessionStorage.getItem('registerEmail') ?? '';
    } catch {
      return '';
    }
  }, []);

  // ---------------------------------------------------------------------
  // Fetch survey config
  // ---------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const client = apiClient();
        const { data, error, response } = await (
          client as unknown as {
            GET: (
              path: string,
              init?: { params?: { query?: Record<string, string> } },
            ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
          }
        ).GET('/api/v1/auth/survey/config', {
          params: { query: { role, locale: 'vi' } },
        });
        if (cancelled) return;
        if (error || !response.ok) {
          const code = extractErrorCode(error) ?? `HTTP_${response.status}`;
          setLoadError(humanizeLoadError(code));
          return;
        }
        const payload = unwrap<SurveyConfig>(data);
        if (!payload || !Array.isArray(payload.questions)) {
          setLoadError(t('survey.errorConfigInvalid'));
          return;
        }
        setConfig(payload);
        // Initialize defaults — `multi` starts as empty array so dirty checks
        // and required validation behave consistently.
        const initial: AnswerMap = {};
        for (const q of payload.questions) {
          if (q.type === 'multi') initial[q.id] = [];
        }
        setAnswers(initial);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : t('survey.errorLoadFailed'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `t` is used only for error strings here; keying on `role` alone is
    // intentional — re-running on locale change would refetch the survey
    // config and reset in-progress answers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // ---------------------------------------------------------------------
  // Answer setters
  // ---------------------------------------------------------------------
  const setAnswer = useCallback((id: string, value: AnswerValue | undefined): void => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const toggleMulti = useCallback(
    (id: string, optionValue: string, checked: boolean): void => {
      setAnswers((prev) => {
        const current = Array.isArray(prev[id]) ? (prev[id] as string[]) : [];
        const next = checked
          ? Array.from(new Set([...current, optionValue]))
          : current.filter((v) => v !== optionValue);
        return { ...prev, [id]: next };
      });
      setErrors((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    [],
  );

  // ---------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------
  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      if (!config) return;
      setSubmitError(null);

      // Required-field validation per question.
      const newErrors: Record<string, string> = {};
      for (const q of config.questions) {
        if (!q.required) continue;
        const v = answers[q.id];
        if (q.type === 'multi') {
          if (!Array.isArray(v) || v.length === 0) {
            newErrors[q.id] = t('survey.errorRequiredMulti');
          }
        } else if (q.type === 'number') {
          if (v === undefined || v === '' || Number.isNaN(Number(v))) {
            newErrors[q.id] = t('survey.errorRequiredNumber');
          }
        } else {
          if (v === undefined || v === null || String(v).trim() === '') {
            newErrors[q.id] = t('survey.errorRequired');
          }
        }
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      if (!email) {
        setSubmitError(t('survey.errorSessionExpired'));
        return;
      }

      setIsSubmitting(true);
      try {
        const idempotencyKey = generateIdempotencyKey();
        const payloadAnswers = config.questions
          .filter((q) => answers[q.id] !== undefined)
          .map((q) => {
            const raw = answers[q.id];
            if (q.type === 'number') {
              return { questionId: q.id, value: Number(raw) };
            }
            return { questionId: q.id, value: raw as string | string[] };
          });

        const client = apiClient();
        const { data, error, response } = await (
          client as unknown as {
            POST: (
              path: string,
              init: { body: unknown; headers?: Record<string, string> },
            ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
          }
        ).POST('/api/v1/auth/survey', {
          body: {
            email: email.trim().toLowerCase(),
            surveyId: config.surveyId,
            version: config.version,
            answers: payloadAnswers,
          },
          headers: {
            'Idempotency-Key': idempotencyKey,
            'Content-Type': 'application/json',
          },
        });

        if (error || !response.ok) {
          const code = extractErrorCode(error) ?? `HTTP_${response.status}`;
          setSubmitError(humanizeSurveyError(code));
          toast({
            variant: 'error',
            title: t('survey.toast.errorSubmitTitle'),
            description: humanizeSurveyError(code),
          });
          return;
        }

        const payload = unwrap<{ newStatus?: 'Active' | 'PendingApproval' }>(data);
        const isPending = payload.newStatus === 'PendingApproval' || role === 'vendor';

        // Clean session — the email key has done its job.
        try {
          sessionStorage.removeItem('registerEmail');
          sessionStorage.removeItem('registerOtpChannel');
          sessionStorage.removeItem('registerResendAvailableAt');
        } catch {
          /* noop */
        }

        toast({
          variant: 'success',
          title: t('survey.toast.successTitle'),
          description: isPending
            ? t('survey.toast.successPending')
            : t('survey.toast.successActive'),
        });
        navigate('/login');
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('survey.toast.errorSubmitFallback');
        setSubmitError(msg);
        toast({ variant: 'error', title: t('survey.toast.errorSubmitTitle'), description: msg });
      } finally {
        setIsSubmitting(false);
      }
    },
    [config, answers, email, navigate, toast, role, t],
  );

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-pw-bg">
        <Spinner size="lg" label={t('survey.loadingLabel')} />
      </main>
    );
  }

  if (loadError || !config) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-pw-bg px-4 py-10">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('survey.loadFailedTitle')}</CardTitle>
            <CardDescription>{loadError ?? t('survey.loadFailedRetry')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              {t('survey.backToLogin')}
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-pw-bg px-4 py-10">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>
            {role === 'merchant'
              ? t('survey.titleMerchant')
              : t('survey.titleVendor')}
          </CardTitle>
          <CardDescription>
            {t('survey.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={onSubmit} noValidate>
            {config.questions.map((q) => (
              <SurveyQuestionField
                key={q.id}
                question={q}
                value={answers[q.id]}
                error={errors[q.id]}
                disabled={isSubmitting}
                onChange={(v) => setAnswer(q.id, v)}
                onToggleMulti={(opt, checked) => toggleMulti(q.id, opt, checked)}
              />
            ))}

            {submitError ? (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
              >
                {submitError}
              </div>
            ) : null}

            <Button
              type="submit"
              variant="default"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner size="sm" /> : t('survey.submit')}
            </Button>
          </Form>
        </CardContent>
      </Card>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Question field renderer
// ---------------------------------------------------------------------------

interface SurveyQuestionFieldProps {
  question: SurveyQuestion;
  value: AnswerValue | undefined;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: AnswerValue | undefined) => void;
  onToggleMulti: (option: string, checked: boolean) => void;
}

function SurveyQuestionField({
  question,
  value,
  error,
  disabled,
  onChange,
  onToggleMulti,
}: SurveyQuestionFieldProps): JSX.Element {
  const labelText = question.required ? `${question.label} *` : question.label;

  if (question.type === 'single') {
    return (
      <FormField>
        <FormLabel>{labelText}</FormLabel>
        <div className="space-y-1">
          {(question.options ?? []).map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 text-sm text-pw-text"
            >
              <input
                type="radio"
                name={question.id}
                value={opt.value}
                checked={value === opt.value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {error ? <FormMessage>{error}</FormMessage> : null}
      </FormField>
    );
  }

  if (question.type === 'multi') {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    return (
      <FormField>
        <FormLabel>{labelText}</FormLabel>
        <div className="space-y-1">
          {(question.options ?? []).map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 text-sm text-pw-text"
            >
              <input
                type="checkbox"
                value={opt.value}
                checked={arr.includes(opt.value)}
                onChange={(e) => onToggleMulti(opt.value, e.target.checked)}
                disabled={disabled}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {error ? <FormMessage>{error}</FormMessage> : null}
      </FormField>
    );
  }

  if (question.type === 'number') {
    return (
      <FormField>
        <FormLabel htmlFor={`q-${question.id}`}>{labelText}</FormLabel>
        <Input
          id={`q-${question.id}`}
          type="number"
          inputMode="numeric"
          placeholder={question.placeholder ?? ''}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) =>
            onChange(e.target.value === '' ? undefined : e.target.value)
          }
          aria-invalid={Boolean(error)}
          disabled={disabled}
        />
        {error ? <FormMessage>{error}</FormMessage> : null}
      </FormField>
    );
  }

  // text — render textarea when label hints longform (>= 60 chars), else input.
  const isLong = question.label.length >= 60;
  return (
    <FormField>
      <FormLabel htmlFor={`q-${question.id}`}>{labelText}</FormLabel>
      {isLong ? (
        <textarea
          id={`q-${question.id}`}
          rows={3}
          placeholder={question.placeholder ?? ''}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          disabled={disabled}
          className="w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text placeholder:text-pw-text-muted/70 focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
        />
      ) : (
        <Input
          id={`q-${question.id}`}
          type="text"
          placeholder={question.placeholder ?? ''}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          disabled={disabled}
        />
      )}
      {error ? <FormMessage>{error}</FormMessage> : null}
    </FormField>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as { success?: boolean }).success === true &&
    'data' in payload
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function extractErrorCode(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'error' in payload &&
    (payload as { error?: { code?: string } }).error?.code
  ) {
    return (payload as { error: { code: string } }).error.code;
  }
  return null;
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function humanizeLoadError(code: string): string {
  switch (code) {
    case 'SURVEY_NOT_FOUND':
      return i18nT('auth:survey.error.surveyNotFound');
    case 'RATE_LIMIT':
      return i18nT('auth:survey.error.rateLimit');
    default:
      return i18nT('auth:survey.error.loadFallback', { code });
  }
}

function humanizeSurveyError(code: string): string {
  switch (code) {
    case 'AUTH_USER_NOT_FOUND':
      return i18nT('auth:survey.error.userNotFound');
    case 'SURVEY_VERSION_STALE':
      return i18nT('auth:survey.error.versionStale');
    case 'SURVEY_INCOMPLETE':
      return i18nT('auth:survey.error.incomplete');
    case 'VALIDATION_FAILED':
      return i18nT('auth:survey.error.validation');
    default:
      return i18nT('auth:survey.error.submitFallback', { code });
  }
}

export default RegisterSurveyPage;
